import { useEffect, useState } from 'react';

function read(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(query).matches;
}

/**
 * Subscribes to a CSS media query from JavaScript.
 *
 * For the cases where the two layouts are structurally different rather than
 * the same boxes at different sizes. The week view is the reason this exists:
 * its phone layout puts lessons in normal flow and its desktop layout puts
 * them at absolute percentage offsets inside an hour grid, so there is no
 * set of CSS rules that turns one into the other. The alternative is
 * rendering both and hiding one, which doubles the DOM and leaves the hidden
 * copy in the accessibility tree.
 *
 * Falls back to false, so a caller asking `(min-width: ...)` gets the narrow
 * layout when there is nothing to ask: server rendering, and jsdom, which
 * evaluates no media queries and reports every one of them as unmatched.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => read(query));

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(query);
    const sync = () => setMatches(list.matches);
    // The query may have changed since the initial state was read, and on the
    // very first render there was no subscription yet either way.
    sync();
    if (typeof list.addEventListener === 'function') {
      list.addEventListener('change', sync);
      return () => list.removeEventListener('change', sync);
    }
    // Safari before 14 has only the deprecated listener pair.
    if (typeof list.addListener === 'function') {
      list.addListener(sync);
      return () => list.removeListener(sync);
    }
    return;
  }, [query]);

  return matches;
}
